import { useTranslation } from '@/composables/useTranslation'
import { useDefaultStore } from '@/store'
import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'
import { UserPrivilege } from '@/models/User'
import ProductsArchiveView from '@/views/ProductsArchiveView.vue'
import ProductView from '@/views/ProductView.vue'
import TranslationsView from '@/views/TranslationsView.vue'
import { toSafeString } from '@/utils/tools'

// Dynamic imports (causes broken references on new versions)
// const ProductsArchiveView = () => import('@/views/ProductsArchiveView.vue')
// const ProductView = () => import('@/views/ProductView.vue')
const ImportView = () => import('@/views/ImportView.vue')
const TaxonomiesView = () => import('@/views/TaxonomiesView.vue')
// const TranslationsView = () => import('@/views/TranslationsView.vue')
const TestView = () => import('@/views/TestView.vue')
const ImageSearchView = () => import('@/views/ImageSearchView.vue')
const PresentationsArchiveView = () => import('@/views/PresentationsArchiveView.vue')
const PresentationView = () => import('@/views/PresentationView.vue')
const EanLabelExport = () => import('@/components/export/ean/EanLabelExportComponent.vue')
const EanLabelGenerator = () => import('@/components/export/ean/EanLabelGenerator.vue')
const UserControlView = () => import('@/views/UserControlView.vue')
const ErrorView = () => import('@/views/ErrorView.vue')
const SignInView = () => import('@/views/SignInView.vue')
const LogsView = () => import('@/views/LogsView.vue')
const AdminPropertiesView = () => import('@/views/AdminPropertiesView.vue')
const ImageTestView = () => import('@/views/ImageTestView.vue')
const ProductComparisonView = () => import('@/views/ProductComparisonView.vue')

type PimRoute = RouteRecordRaw & {
  meta?: {
    title: string
    requiresPrivilege?: UserPrivilege[]
    requiresSuperAdmin?: boolean
    showInAdminMenu?: boolean | UserPrivilege
    alwaysReload?: boolean // always reload on entering route
    fullscreen?: boolean // does not show topmenu
    componentName?: string
    fullWidth?: boolean // does not use scrollbar margin and width trick
  }
}

const routes: PimRoute[] = [
  {
    path: '/sign-in',
    name: 'sign-in',
    component: SignInView,
    meta: {
      title: 'Logga in',
    },
  },
  {
    path: '/products',
    name: 'products',
    component: ProductsArchiveView,
    meta: {
      title: 'Artikelarkiv',
      fullWidth: true,
    },
  },
  {
    path: '/products/:sku',
    name: 'product',
    props: (route) => ({
      sku: route.params.sku,
      modal: history.state.modal,
    }),
    component: ProductView,
    meta: {
      title: 'Artikel',
    },
  },
  {
    path: '/new-product',
    name: 'new-product',
    props: () => ({
      modal: history.state.modal,
    }),
    component: ProductView,
    meta: {
      title: 'Ny artikel',
    },
  },
  {
    path: '/taxonomies',
    name: 'taxonomies',
    component: TaxonomiesView,
    meta: {
      requiresPrivilege: ['manageTaxonomies'],
      title: 'Taxonomier',
      showInAdminMenu: true,
      alwaysReload: true,
    },
  },
  {
    path: '/translations',
    name: 'translations',
    props: true,
    component: TranslationsView,
    meta: {
      title: 'Översättningar',
      showInAdminMenu: true,
      alwaysReload: true,
      requiresPrivilege: ['manageTranslations'],
    },
  },
  {
    path: '/import',
    name: 'import',
    component: ImportView,
    meta: {
      requiresPrivilege: ['manageProductImports', 'manageTranslationImports'],
      title: 'Importera',
      showInAdminMenu: true,
    },
  },
  {
    path: '/presentations',
    name: 'presentations',
    component: PresentationsArchiveView,
    meta: {
      title: 'Presentationer',
      showInAdminMenu: 'managePresentations',
    },
  },
  {
    // viewing a page separately not through a composition
    path: '/presentations/page/:pageId',
    name: 'presentationPage',
    component: PresentationView,
    meta: {
      title: 'Presentationssida',
    },
  },
  {
    path: '/presentations/:compositionId/:page?',
    name: 'presentation',
    props: true,
    component: PresentationView,
    meta: {
      title: 'Presentation',
      fullscreen: true,
    },
  },
  {
    path: '/products/ean/:type',
    name: 'product-ean',
    component: EanLabelExport,
    meta: {
      title: 'EAN Labels',
    },
  },
  {
    path: '/users',
    name: 'user-control',
    component: UserControlView,
    meta: {
      requiresSuperAdmin: true,
      title: 'Användarkontroll',
      showInAdminMenu: true,
    },
  },
  {
    path: '/logs',
    name: 'logs',
    component: LogsView,
    meta: {
      requiresPrivilege: ['manageLogs'],
      title: 'Administrationsloggar',
      showInAdminMenu: true,
    },
  },
  {
    path: '/properties',
    name: 'properties',
    component: AdminPropertiesView,
    meta: {
      requiresPrivilege: ['manageProperties'],
      title: 'Fält',
      showInAdminMenu: true,
    },
  },
  {
    path: '/image-search',
    name: 'image-search',
    component: ImageSearchView,
    meta: {
      title: 'Bildsök',
    },
  },
  {
    path: '/ean-label-tester',
    name: 'ean-label-tester',
    component: EanLabelGenerator,
    meta: {
      title: 'EAN-etikett-tester',
    },
  },
  {
    path: '/compare',
    name: 'product-comparison',
    component: ProductComparisonView,
    meta: {
      title: 'Jämför artiklar',
      requiresPrivilege: ['manageProducts'],
    },
  },
  {
    path: '/test',
    component: TestView,
    meta: {
      requiresPrivilege: ['accessToDevTools'],
      title: 'Test',
    },
  },
  {
    path: '/image-test',
    component: ImageTestView,
    meta: {
      requiresSuperAdmin: true,
      title: 'Image test',
    },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: ErrorView,
    props: (route) => ({
      title: '404: Sidan hittades inte',
      text: `Sidan "${route.path}" verkar inte finnas`,
    }),
    meta: {
      title: '404: Page not found',
    },
  },
]

export const adminRoutesInTopMenu = routes
  .filter((route) => route.meta?.showInAdminMenu)
  .sort((a, b) => (a.meta?.title as string)?.localeCompare(b.meta?.title as string))

export const keepAliveComponents = [
  'ProductsArchive',
  'TaxonomiesView',
  'TranslationsView',
  'ImportView',
  'PresentationsArchiveView',
  'UserControlView',
  'LogsView',
  'AdminPropertiesView',
]

export const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
})

router.onError((error, to) => {
  if (error.message.includes('Failed to fetch dynamically imported module') || error.message.includes('Importing a module script failed')) {
    console.log('Reloading application')
    window.location.href = to.fullPath
  }
})

router.beforeEach(async (to, from, next) => {
  const { appTranslate } = useTranslation()
  const store = useDefaultStore()

  // Navigation confirmation guard
  if (store.confirmNavigationFrom && !store.temporaryIgnoreNavigationBlock) {
    // legacy navigation confirm
    if (!confirm(appTranslate('Your changes might not have been saved. Are you sure you want to leave the page?'))) {
      next(false)
      return
    } else {
      store.confirmNavigationFrom = undefined
    }
  } else if (store.evaluateNavigation && !store.temporaryIgnoreNavigationBlock) {
    // new navigation evaluation with callback
    if (await store.evaluateNavigation.cb?.()) {
      store.evaluateNavigation = undefined
    } else {
      next(false)
      return
    }
  }

  // Route sign in state guard
  if (store.isSignedIn) {
    const intendedRoute = to.query.redirect?.toString()
    if (to.name === 'sign-in' || to.path === '/') {
      next(intendedRoute || { name: 'products' })
      return
    }
  } else if (to.name !== 'sign-in') {
    const redirect = to.fullPath !== '/' ? to.fullPath : undefined
    next({ name: 'sign-in', query: { redirect } })
    return
  }

  // Auth guard
  const requiredPrivileges = to.meta.requiresPrivilege as UserPrivilege[]

  const isAuthorized = requiredPrivileges
    ? requiredPrivileges.some((privilege) => store.userPrivileges?.has(privilege))
    : to.meta.requiresSuperAdmin
      ? store.currentUser?.role === 'superAdmin'
      : true

  if (!isAuthorized && !to.query.error) {
    next({ ...to, query: { error: 'unauthorized' } })
    return
  } else if (isAuthorized && to.query.error) {
    const query = to.query
    delete query.error
    next({ ...to, query })
    return
  }

  // Checks passed
  next()
})

router.afterEach((to) => {
  if (to.matched.length) {
    setDocumentTitle(toSafeString(to.meta.title))
  }
})

export const setDocumentTitle = (title: string) => {
  const store = useDefaultStore()

  const environment = store.localEnvironment !== 'production' ? store.localEnvironment.toUpperCase() : ''
  document.title = `${title} - Burde PIM ${environment} `
}

/**
 * Use to trigger router navigation guard
 * @returns true if navigation succeeds
 */
export const triggerNavigation = async () => {
  const navFail = await router?.replace({ ...router.currentRoute.value, force: true })
  return !navFail
}

export default router
